import { useState } from "react";
import { Camera, Loader2, Phone } from "lucide-react";
import { toast } from "sonner";
import { MapView } from "@/components/MapView";
import { StatusTimeline } from "@/components/StatusTimeline";
import { ChatButton, CallButton } from "@/components/TripChat";
import { WaitTimer } from "@/components/WaitTimer";
import { api, errMsg, money, fmtDate } from "@/lib/api";
import { FoodSummary } from "@/components/FoodRequest";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const DriverCancel = ({ arrived, busy, onConfirm }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild><button data-testid="driver-cancel-trip-button" disabled={busy} className="mt-3 w-full text-center text-xs text-zinc-500 underline hover:text-red-300">Cancel this trip</button></AlertDialogTrigger>
    <AlertDialogContent data-testid="driver-cancel-dialog">
      <AlertDialogHeader>
        <AlertDialogTitle>Cancel this trip?</AlertDialogTitle>
        <AlertDialogDescription>{arrived ? "You've arrived at the pickup, so this counts toward the cancellation rules: 2 in a month gives a warning, 3 puts your contract under review." : "The trip goes back to dispatch for another driver."}</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="driver-cancel-keep">Keep trip</AlertDialogCancel>
        <AlertDialogAction data-testid="driver-cancel-confirm" onClick={onConfirm}>Cancel trip</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const ACTIONS = {
  accepted: ["arrived", "Arrived at pickup", "driver-arrived-button"],
  arrived: ["start", "Start trip", "driver-start-trip-button"],
  in_progress: ["complete", "Complete trip", "driver-complete-trip-button"],
};

const DELIVERY_ACTIONS = {
  accepted: ["arrived", "Arrived at store", "driver-arrived-button"],
  arrived: ["start", "Picked up", "driver-start-trip-button"],
  in_progress: ["complete", "Delivered", "driver-complete-trip-button"],
};

const DeliveryPhoto = ({ trip }) => {
  const [state, setState] = useState(trip.delivery?.photo_path ? "done" : "idle");
  if (trip.kind !== "delivery" || trip.status !== "in_progress") return null;
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setState("busy");
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post(`/driver/trips/${trip.id}/delivery-photo`, fd); setState("done"); } catch (err) { toast.error(errMsg(err)); setState("idle"); }
  };
  return (
    <label className="mt-4 flex cursor-pointer items-center gap-2 rounded-xl border border-dashed border-amber-500/30 p-3 text-sm text-zinc-300" data-testid="delivery-photo-upload">
      <Camera className="h-4 w-4 text-amber-400" />
      {state === "done" ? "Delivery photo added" : state === "busy" ? "Uploading…" : "Add delivery photo (optional)"}
      <input type="file" accept="image/jpeg,image/png,image/webp" capture="environment" data-testid="delivery-photo-input" className="hidden" onChange={upload} />
    </label>
  );
};

const AlcoholIdCheck = ({ trip, onAction, busy }) => {
  const [ok, setOk] = useState(false);
  const f = trip.food || {};
  if (!f.includes_alcohol || f.id_status !== "verified" || !["accepted", "arrived"].includes(trip.status)) return null;
  if (f.driver_id_check) {
    return <p data-testid="driver-id-check-done" className={`mt-4 text-sm ${f.driver_id_check.result === "verified" ? "text-emerald-300" : "text-red-300"}`}>
      {f.driver_id_check.result === "verified" ? "Physical ID checked — 21+ and matches" : "ID doesn't match — do not hand over the alcohol. The company was alerted."}</p>;
  }
  return (
    <div data-testid="driver-id-check" className="mt-4 space-y-3 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm">
      <p className="font-semibold text-amber-100">Alcohol on this trip — check the customer's physical ID before starting</p>
      <label className="flex items-start gap-2 text-zinc-200">
        <input type="checkbox" data-testid="driver-id-check-checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} className="mt-0.5 h-4 w-4 accent-amber-400" />
        I checked the customer's physical ID — 21+ and matches
      </label>
      <div className="flex flex-wrap gap-2">
        <button data-testid="driver-id-check-confirm" disabled={!ok || busy} onClick={() => onAction("alcohol-id-check", { result: "verified" })} className="btn-gold !px-4 !py-2 text-xs">Confirm ID check</button>
        <button data-testid="driver-id-mismatch-button" disabled={busy} onClick={() => onAction("alcohol-id-check", { result: "mismatch" })} className="btn-danger !px-4 !py-2 text-xs">ID doesn't match</button>
      </div>
    </div>
  );
};

export const DriverTripPanel = ({ trip, position, onAction, busy, fees }) => {
  const [action, label, testId] = (trip.kind === "delivery" ? DELIVERY_ACTIONS : ACTIONS)[trip.status] || [];
  const rental = trip.kind === "rental";
  const dest = trip.stops[trip.stops.length - 1] || trip.pickup;
  const f = trip.food || {};
  const idPending = f.includes_alcohol && f.id_status === "verified" && !f.driver_id_check;
  const blocked = action === "start" && idPending;
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,420px)_1fr] lg:gap-8" data-testid="driver-active-trip">
      <div className="order-2 min-w-0 space-y-6 lg:order-1">
        <div className="lux-card p-5 sm:p-6">
          <p className="lux-eyebrow mb-6">Active trip</p>
          <StatusTimeline status={trip.status} kind={trip.kind} />
          <div className="mt-8 flex items-center gap-3 rounded-xl bg-white/[0.03] p-4">
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-amber-100" data-testid="driver-trip-customer">{trip.customer_name}</p>
              <p className="text-sm text-zinc-400" data-testid="driver-trip-summary">{rental ? `Rental · ${trip.rental?.hours}h from ${fmtDate(trip.rental?.start_at)}` : `${trip.trip_miles} mi · ${trip.trip_minutes} min`} · {money(trip.fare.total)}</p>
            </div>
            <ChatButton tripId={trip.id} myRole="driver" title={`Chat with ${trip.customer_name}`} />
            <CallButton tripId={trip.id} phone={trip.customer_phone} label="Call customer" />
          </div>
          {trip.status === "arrived" && !rental && trip.kind !== "delivery" && trip.timestamps?.arrived_at && <div className="mt-4"><WaitTimer arrivedAt={trip.timestamps.arrived_at} fees={fees} /></div>}
          {trip.kind === "delivery" && <p data-testid="driver-delivery-info" className="mt-3 text-sm text-zinc-300">DELIVERY · {trip.delivery?.business_name}: {trip.delivery?.items}{trip.delivery?.notes ? ` · ${trip.delivery.notes}` : ""}</p>}
          {trip.live_eta_min != null && trip.status !== "arrived" && (
            <p className="mt-4 text-sm text-zinc-400">ETA to {trip.live_eta_target}: <span data-testid="driver-live-eta" className="text-amber-200">{Math.max(1, Math.round(trip.live_eta_min))} min</span>
              {trip.miles_to_pickup != null && <span className="ml-2 text-zinc-500">· pickup distance at accept {trip.miles_to_pickup} mi</span>}</p>
          )}
          <AlcoholIdCheck trip={trip} onAction={onAction} busy={busy} />
          <DeliveryPhoto trip={trip} />
          {action && (
            <button data-testid={testId} disabled={busy || blocked} onClick={() => onAction(action)} className="btn-gold mt-6 w-full !py-4 text-base">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} {label}
            </button>
          )}
          {["accepted", "arrived"].includes(trip.status) && <DriverCancel arrived={trip.status === "arrived"} busy={busy} onConfirm={() => onAction("cancel")} />}
        </div>
        <FoodSummary trip={trip} />
        <div className="lux-card space-y-3 p-5 text-sm sm:p-6">
          <p className="lux-eyebrow">Route</p>
          <p className="text-zinc-200"><span className="mr-2 text-amber-400">P</span>{trip.pickup.address}</p>
          {trip.stops.map((s, i) => <p key={i} className="text-zinc-400"><span className="mr-2 text-amber-400">{i + 1}</span>{s.address}</p>)}
          <a href={`https://www.openstreetmap.org/directions?route=${trip.pickup.lat},${trip.pickup.lng};${dest.lat},${dest.lng}`} target="_blank" rel="noreferrer" data-testid="open-navigation-link" className="inline-block pt-2 text-amber-300 hover:text-amber-200">Open turn-by-turn in OpenStreetMap →</a>
        </div>
      </div>
      <div className="order-1 min-w-0 lg:order-2 lg:sticky lg:top-28 lg:self-start">
        <MapView className="h-[340px] sm:h-[440px] lg:h-[calc(100vh-10rem)]" pickup={trip.pickup} stops={trip.stops} route={trip.route_geometry} driver={position} customer={trip.customer_location} testId="driver-trip-map" />
        {trip.customer_location_off && <p data-testid="customer-location-off" className="mt-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-200">Customer location off — use the pickup address</p>}
      </div>
    </div>
  );
};

export const PhoneLink = ({ phone }) => phone ? <a href={`tel:${phone}`}><Phone className="h-4 w-4" /></a> : null;
